import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useRole } from '../contexts/RoleContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';

interface Customer {
  id: number;
  name: string;
  phone_number: string;
  caregiver_name: string;
  caregiver_phone: string;
  caregiver_email?: string;
  custom_message?: string;
  is_active: boolean;
  created_at: string;
}

const MyLovedOnes: React.FC = () => {
  const apiUrl = getApiUrl();
  const { isEndUser } = useRole();
  const { token } = useAuth();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Customer>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEndUser) {
      fetchMyCustomers();
    }
  }, [isEndUser, token]);

  const fetchMyCustomers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrl}/customers/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(Array.isArray(data) ? data : []);
        setError(null);
      } else {
        let detail = 'Failed to fetch your assigned customers';
        try {
          const errorData = await response.json();
          detail = errorData?.detail || detail;
        } catch {
          // Keep default error for non-JSON response bodies.
        }
        setError(detail);
      }
    } catch (err) {
      setError('An error occurred while fetching customers');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setViewDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setViewDialogOpen(false);
    setSelectedCustomer(null);
  };

  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEditForm(customer);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedCustomer(null);
    setEditForm({});
  };

  const handleSaveCustomer = async () => {
    if (!selectedCustomer) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${apiUrl}/customers/${selectedCustomer.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (response.ok) {
        // Update the local state
        setCustomers(prev => 
          prev.map(customer => 
            customer.id === selectedCustomer.id 
              ? { ...customer, ...editForm }
              : customer
          )
        );
        handleCloseEditDialog();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update customer');
      }
    } catch (err) {
      setError('Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  if (!isEndUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Access denied. This page is only available for end users.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        My Loved Ones
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View and manage the loved ones you are assigned to care for.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {customers.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" color="text.secondary" align="center">
              No loved ones assigned to you yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Contact your administrator to get assigned to loved ones.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {customers.map((customer) => (
            <Grid item xs={12} sm={6} md={4} key={customer.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="h2">
                      {customer.name}
                    </Typography>
                    <Chip
                      label={customer.is_active ? 'Active' : 'Inactive'}
                      color={customer.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{ ml: 'auto' }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {customer.phone_number}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Caregiver: {customer.caregiver_name}
                      </Typography>
                    </Box>
                    {customer.caregiver_email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {customer.caregiver_email}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {customer.custom_message && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Custom Message:</strong> {customer.custom_message}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewCustomer(customer)}
                    sx={{ flex: 1 }}
                  >
                    View
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditCustomer(customer)}
                    sx={{ flex: 1 }}
                  >
                    Edit
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* View Customer Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedCustomer?.name} - Details
        </DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  value={selectedCustomer.name}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  value={selectedCustomer.phone_number}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Caregiver Name"
                  value={selectedCustomer.caregiver_name}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Caregiver Phone"
                  value={selectedCustomer.caregiver_phone}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              {selectedCustomer.caregiver_email && (
                <Grid item xs={12}>
                  <TextField
                    label="Caregiver Email"
                    value={selectedCustomer.caregiver_email}
                    fullWidth
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              {selectedCustomer.custom_message && (
                <Grid item xs={12}>
                  <TextField
                    label="Custom Message"
                    value={selectedCustomer.custom_message}
                    fullWidth
                    multiline
                    rows={3}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Status"
                  value={selectedCustomer.is_active ? 'Active' : 'Inactive'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Created"
                  value={new Date(selectedCustomer.created_at).toLocaleDateString()}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit {selectedCustomer?.name}
        </DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone Number"
                  value={editForm.phone_number || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, phone_number: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Caregiver Name"
                  value={editForm.caregiver_name || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, caregiver_name: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Caregiver Phone"
                  value={editForm.caregiver_phone || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, caregiver_phone: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Caregiver Email"
                  value={editForm.caregiver_email || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, caregiver_email: e.target.value }))}
                  fullWidth
                  type="email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Custom Message"
                  value={editForm.custom_message || ''}
                  onChange={(e) => setEditForm(prev => ({ ...prev, custom_message: e.target.value }))}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCustomer} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyLovedOnes;
